import React from 'react';
import PropTypes from 'prop-types';
import { getClickAttrs } from '../Analytics/gtag';
import styles from './popOverMenuMultiLevel.scss';

class PopOverMenuMultiLevel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isMenuShowed: false,
            displayMenuBelowAnchor: true,
            hoveredMenuPath: [], // Track hovered menu items across levels
        };
        this.handleAchorClick = this.handleAchorClick.bind(this);
        this.setWrapperRef = this.setWrapperRef.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.menuItemClick = this.menuItemClick.bind(this);
        this.handleMouseEnter = this.handleMouseEnter.bind(this); // For multi-level menu
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
        document.addEventListener('touchend', this.handleClickOutside, false);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        document.removeEventListener('touchend', this.handleClickOutside, false);
    }

    setWrapperRef(node) {
        this.wrapperRef = node;
    }

    handleAchorClick(ev) {
        ev.preventDefault();
        ev.stopPropagation();

        // Dynamically decide if the menu should display below or above the anchor
        let showBelow = true;
        if (ev.nativeEvent.clientY > (2 * window.innerHeight / 3)) {
            showBelow = false;
        }

        const { alignLeft } = this.props;
        this.setState({
            isMenuShowed: true,
            displayMenuBelowAnchor: showBelow,
            alignLeft,
        });
    }

    handleClickOutside(event) {
        if (this.wrapperRef && !this.wrapperRef.contains(event.target) && this.state.isMenuShowed) {
            event.stopPropagation();
            event.preventDefault();
            this.setState({
                isMenuShowed: false,
                hoveredMenuPath: [], // Reset hover state
            });
        }
    }

    handleMouseEnter(menuItem, level) {
        this.setState((prevState) => {
            const newHoveredMenuPath = prevState.hoveredMenuPath.slice(0, level);
            newHoveredMenuPath[level] = menuItem;
            return { hoveredMenuPath: newHoveredMenuPath };
        });
    }

    handleMouseLeave(level) {
        this.setState((prevState) => {
            const newHoveredMenuPath = prevState.hoveredMenuPath.slice(0, level);
            return { hoveredMenuPath: newHoveredMenuPath };
        });
    }

    menuItemClick(menuItem) {
        return (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            if (this.props.onSelected) {
                this.props.onSelected(menuItem, ev);
            }
            this.setState({
                isMenuShowed: false,
                hoveredMenuPath: [], // Close all menus on selection
            });
        };
    }

    // Recursive function to render menu and submenus
    renderMenuItems(menuItems, level = 0) {
        const { hoveredMenuPath } = this.state;
        const hoveredMenuItem = hoveredMenuPath[level];

        return (
            <div id="list" className={`popovermenu-list ${this.props.popOverMenuClass || ''}`}>
                {menuItems?.map((menuItem) => {
                    const getMenuItemClassName = (
                        (menuItem?.divisionName && this.props.selectedValue === menuItem.divisionName) ||
                        (this.props.selectedValue && menuItem.value === this.props.selectedValue)
                    ) ? 'text-blue' : '';

                    return menuItem && (menuItem.show !== false) ? (
                        <div
                            key={menuItem.value}
                            className="menu-item"
                            onMouseEnter={() => this.handleMouseEnter(menuItem, level)} // Track hover at the current level
                            onMouseLeave={() => this.handleMouseLeave(level)} // Clear hover when leaving the level
                            onClick={this.menuItemClick(menuItem)}
                            {...getClickAttrs({ // Keep tracking clicks with analytics
                                category: menuItem.clickCategory,
                                context: menuItem.clickContext,
                                location: menuItem.clickLocation,
                            })}
                        >
                            <div className={getMenuItemClassName}>{menuItem.label}</div>

                            {/* Render next-level submenu if the current item is hovered and has a submenu */}
                            {hoveredMenuItem === menuItem && menuItem.subMenu && (
                                <div className="submenu">
                                    {this.renderMenuItems(menuItem.subMenu, level + 1)}
                                </div>
                            )}
                        </div>
                    ) : null;
                })}
            </div>
        );
    }

    render() {
        return (
            <div className={`${styles.popOverMenu} ${this.props.popOverMenuClass || ''}`}>
                <div
                    className={`popovermenu-anchor ${this.props.anchorClass || ''}`}
                    onClick={this.handleAchorClick}
                >
                    {this.props.children || <span className={`far ${this.props.horizontalEllipsis ? 'fa-ellipsis-h' : 'fa-ellipsis-v'}`} />}
                </div>

                {this.state.isMenuShowed && <div className="anchor-click-disabler" />}

                <div
                    className={`menu-container ${this.state.displayMenuBelowAnchor ? 'below-anchor' : 'above-anchor'} ${this.state.alignLeft ? 'align-left' : 'align-right'} ${this.props.mobileAlignLeft}`}
                    ref={this.setWrapperRef}
                >
                    {this.state.isMenuShowed && this.renderMenuItems(this.props.menuItems)}
                </div>
            </div>
        );
    }
}

PopOverMenuMultiLevel.propTypes = {
    mobileAlignLeft: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
    ]),
    menuItems: PropTypes.arrayOf(PropTypes.any).isRequired,
    selectedValue: PropTypes.string,
    alignLeft: PropTypes.bool,
    onSelected: PropTypes.func.isRequired,
    popOverMenuClass: PropTypes.string,
    anchorClass: PropTypes.string,
    horizontalEllipsis: PropTypes.bool,
    children: PropTypes.node,
};

PopOverMenuMultiLevel.defaultProps = {
    mobileAlignLeft: false,
    selectedValue: '',
    alignLeft: false,
    popOverMenuClass: '',
    anchorClass: '',
    horizontalEllipsis: false,
    children: null,
};

export default PopOverMenuMultiLevel;
